import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, Crosshair, Loader2, X } from "lucide-react";

export const Header = ({ onLocate, locating, totalStores }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const updateQuery = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set("q", v);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-black/95 backdrop-blur-xl border-b border-zinc-900"
    >
      <div className="px-4 sm:px-6 lg:px-10 py-4">
        <div className="flex items-center gap-3 sm:gap-5 lg:gap-8">
          {/* LOGO */}
          <Link
            to="/"
            data-testid="logo-link"
            className="flex items-center gap-2.5 shrink-0 group"
          >
            <div className="relative w-9 h-9 rounded-lg overflow-hidden bg-white shrink-0">
              <div className="absolute inset-0 flex">
                <div className="w-1/5 bg-black" />
                <div className="w-1/5 bg-white" />
                <div className="w-1/5 bg-black" />
                <div className="w-1/5 bg-white" />
                <div className="w-1/5 bg-black" />
              </div>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-display font-extrabold text-xl tracking-tight text-white">
                ZEBRA<span className="text-[#FF5612]">LEARN</span>
              </span>
              <span className="text-[10px] text-zinc-500 font-medium tracking-[0.18em] uppercase mt-1">
                Store Locator
              </span>
            </div>
          </Link>

          {/* SEARCH — prominent pill bar (only on /) */}
          {!isAdmin && (
            <div className="flex-1 max-w-3xl mx-auto">
              <div
                data-testid="header-search-wrap"
                className="relative flex items-center bg-zinc-900/80 hover:bg-zinc-900 focus-within:bg-zinc-900 border border-zinc-800 focus-within:border-[#FF5612] rounded-full transition-colors px-5 py-3 group"
              >
                <Search className="w-4 h-4 sm:w-5 sm:h-5 text-zinc-400 group-focus-within:text-[#FF5612] shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => updateQuery(e.target.value)}
                  placeholder="Search stores by name, city, address…"
                  data-testid="header-search-input"
                  className="flex-1 bg-transparent ml-3 outline-none text-white placeholder:text-zinc-500 text-sm sm:text-base"
                />
                {query && (
                  <button
                    onClick={() => updateQuery("")}
                    data-testid="header-search-clear"
                    aria-label="Clear search"
                    className="text-zinc-400 hover:text-white shrink-0 mr-2"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {onLocate && (
                  <button
                    onClick={onLocate}
                    disabled={locating}
                    data-testid="header-locate-btn"
                    className="hidden sm:inline-flex items-center gap-2 bg-[#FF5612] hover:bg-[#FF6B2B] text-white text-xs sm:text-sm font-semibold rounded-full px-4 py-2 transition-colors disabled:opacity-60 shrink-0 -mr-1"
                  >
                    {locating ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Crosshair className="w-3.5 h-3.5" />
                    )}
                    Near Me
                  </button>
                )}
              </div>
            </div>
          )}

          {/* RIGHT NAV */}
          <nav className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {totalStores != null && !isAdmin && (
              <span className="hidden lg:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 text-xs font-medium text-zinc-300">
                <MapPin className="w-3 h-3 text-[#FF5612]" /> {totalStores} stores
              </span>
            )}
            <a
              href="https://zebralearn.com"
              target="_blank"
              rel="noreferrer"
              data-testid="visit-main-site"
              className="hidden md:inline text-sm font-medium text-zinc-300 hover:text-white transition-colors px-3"
            >
              Main Site
            </a>
            {isAdmin && (
              <button
                onClick={() => navigate("/")}
                data-testid="back-to-locator"
                className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-white bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 px-3 sm:px-4 py-2 rounded-full transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" /> Locator
              </button>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
